import { Label } from "../../../components/custom/programs/fields";
import { useEffect, useMemo, useState } from "react";
import { Badge, Col, Form, Nav, Row } from "react-bootstrap";
import { Flex } from "../../../../styled-system/jsx";
import { Button, LinkButton } from "../../../components/core/basic/Button";
import { useDebounce } from "@reactuses/core";
import { css } from "../../../../styled-system/css";
import logo from "../../../assets/images/logo/Trellis_Logomark_color.png";
import { MdNavigateNext, MdNavigateBefore, MdFirstPage, MdLastPage } from "react-icons/md";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { Modal } from "../../../components/core/basic/Modal";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGE_SIZE = 15;
const MAX_TARGETS_TO_SHOW = 3;

const DSPTargetSelector = ({
  title,
  setSearchQuery = () => {},
  options = [],
  selectedTargets = [],
  selectedExclusions = [],
  renderTargetMode = true,
  renderExcludeMode = false,
  onAdd = () => {},
  onRemove = () => {},
  onReset = () => {},
  searchResultsLoading = false,
  selectionMode,
  setSelectionMode,
  additionalTabs = [],
  hideSearchField = false,
  customEmptyResultsMessage = null,
  customLabelFormatter = null,
  pageSize = DEFAULT_PAGE_SIZE,
  staticData = false,
}) => {
  const { t } = useTranslation(["dsp"]);
  const resolvedTitle = title ?? t(($) => $.dsp["Location Targets"]);
  const [search, setSearch] = useState("");
  const [showSelector, setShowSelector] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearchFilter = useDebounce(search, 300);

  useEffect(() => {
    setSearchQuery(debouncedSearchFilter);
    setPage(1);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchFilter]);

  const pageOptions = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return options?.slice(start, end);
  }, [page, options, pageSize]);

  return (
    <Form.Group>
      <Flex gap="large" alignItems={"center"} justifyContent={"space-between"}>
        <Label className={css({ paddingTop: "10px" })}>{resolvedTitle}</Label>
        <div>
          <LinkButton
            onClick={() => {
              if (showSelector) {
                setSearch("");
                setSearchQuery("");
              }
              setShowSelector(!showSelector);
            }}
          >
            {selectedTargets?.length ? t(($) => $.dsp["Modify"]) : t(($) => $.dsp["Add Targets"])}
          </LinkButton>
        </div>
      </Flex>
      <Flex direction="column" className="mb-4">
        <p className={css({ fontSize: "14px" })}>
          {t(($) => $.dsp["{count} Selected Target{count, plural, one {} other {s}}"], {
            count: selectedTargets?.length ?? 0,
          })}
        </p>

        {selectedTargets?.slice(0, MAX_TARGETS_TO_SHOW)?.map((target, i) => {
          return (
            <Checkbox
              key={`mainFormTargets-${target.value}-${i}`}
              value={true}
              label={target.label}
              onChange={() => {
                onRemove(target.value, "target");
              }}
            />
          );
        })}
        {selectedTargets.length > MAX_TARGETS_TO_SHOW ?
          <small className={css({ paddingStart: "36px" })}>
            +{" "}
            {t(($) => $.dsp["{count, plural, one {1 more} other {# more}}"], {
              count: selectedTargets.length - MAX_TARGETS_TO_SHOW,
            })}
          </small>
        : null}
        {selectedExclusions?.length ?
          <>
            <p className={css({ fontSize: "14px" })}>
              {t(($) => $.dsp["{count} Selected Exclusion{count, plural, one {} other {s}}"], {
                count: selectedExclusions?.length ?? 0,
              })}
            </p>
            {selectedExclusions?.slice(0, MAX_TARGETS_TO_SHOW)?.map((exclusion, i) => (
              <Checkbox
                key={`mainFormExclusions-${exclusion.value}-${i}`}
                value={true}
                label={exclusion.label}
                onChange={() => {
                  onRemove(exclusion.value, "exclusion");
                }}
              />
            ))}
            {selectedExclusions.length > MAX_TARGETS_TO_SHOW ?
              <small className={css({ paddingStart: "36px" })}>
                +{" "}
                {t(($) => $.dsp["{count, plural, one {1 more} other {# more}}"], {
                  count: selectedExclusions.length - MAX_TARGETS_TO_SHOW,
                })}
              </small>
            : null}
          </>
        : null}
      </Flex>
      <Flex direction={"column"}>
        <Modal
          isOpen={showSelector}
          onOpenChange={(isOpen) => setShowSelector(isOpen)}
          title={t(($) => $.dsp["Update Line Item {title}"], { title: resolvedTitle })}
          css={{
            width: "70vw",
            maxHeight: "95vh",
            paddingRight: "xlarge",
          }}
        >
          <div className="dsp-target-selector">
            <Row className={css({ visibility: hideSearchField ? "hidden" : "inherit" })}>
              <Col xs={12} lg={{ span: 8, offset: 2 }}>
                <Form.Control
                  placeholder={t(($) => $.dsp["Search..."])}
                  aria-label={t(($) => $.dsp["Search Term"])}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  value={search}
                  style={{ width: "90%" }}
                  className="mt-3 mb-5  mx-auto"
                />
              </Col>
            </Row>

            <Row className="py-3 px-5" style={{ margin: "0 50px" }}>
              <Col xs={12} lg={8}>
                <div
                  style={{ overflowY: "auto", height: "580px" }}
                  className="pe-5 custom-scroll-container category-target-search-scroll-container"
                >
                  <Flex gap="large">
                    <Nav variant="underline" activeKey={selectionMode} onSelect={(mode) => setSelectionMode(mode)}>
                      {renderTargetMode ?
                        <Nav.Item key={"target"}>
                          <Nav.Link eventKey={"target"}>{t(($) => $.dsp["Target"])}</Nav.Link>
                        </Nav.Item>
                      : null}
                      {renderExcludeMode ?
                        <Nav.Item key={"exclude"}>
                          <Nav.Link eventKey={"exclude"}>{t(($) => $.dsp["Exclude"])}</Nav.Link>
                        </Nav.Item>
                      : null}
                      {additionalTabs?.map((tab) => (
                        <Nav.Item key={tab.id}>
                          <Nav.Link eventKey={tab.id}>{tab.label}</Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                  </Flex>
                  {!["target", "exclude"].includes(selectionMode) ?
                    <>
                      {additionalTabs?.find((tab) => tab.id === selectionMode)?.content ?? (
                        <p>{t(($) => $.dsp["Placeholder"])}</p>
                      )}
                    </>
                  : searchResultsLoading ?
                    <Flex justifyContent={"center"} alignItems={"center"} className="w-100 h-75">
                      <img src={logo} alt="" className="rotate" width="80" height="auto" />
                    </Flex>
                  : !searchResultsLoading && !search && !staticData ?
                    <Flex alignItems={"center"} className="p-5">
                      <p className="w-100 py-5 text-center fs-standard">
                        {customEmptyResultsMessage ?
                          customEmptyResultsMessage
                        : selectionMode === "target" ?
                          t(($) => $.dsp["Enter a search term above to select targets"])
                        : t(($) => $.dsp["Enter a search term above to select exclusions"])}
                      </p>
                    </Flex>
                  : <Flex direction={"column"} className="pt-0 overflow-auto">
                      {options?.length ?
                        <Flex alignItems="center" justifyContent={"flex-end"}>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              setPage(1);
                            }}
                          >
                            <MdFirstPage />
                          </button>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              if (page > 1) {
                                setPage(page - 1);
                              }
                            }}
                          >
                            <MdNavigateBefore />
                          </button>
                          <span className="fs-5">
                            {page} / {Math.ceil(options?.length / pageSize)}
                          </span>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              if (page < options?.length / pageSize) {
                                setPage(page + 1);
                              }
                            }}
                          >
                            <MdNavigateNext />
                          </button>
                          <button
                            style={{
                              background: "#fff",
                              color: "#000",
                            }}
                            className="btn-round m-0"
                            onClick={() => {
                              setPage(Math.ceil(options?.length / pageSize));
                            }}
                          >
                            <MdLastPage />
                          </button>
                        </Flex>
                      : null}
                      {pageOptions?.map((option) => {
                        const { label, value } = option;

                        return (
                          <Checkbox
                            key={value}
                            value={
                              selectionMode === "exclude" ?
                                selectedExclusions?.map((exclusion) => exclusion.value).includes(value)
                              : selectedTargets?.map((target) => target.value).includes(value)
                            }
                            label={customLabelFormatter ? customLabelFormatter(option) : label}
                            onChange={(checked) => {
                              if (checked) {
                                onAdd(value);
                              } else {
                                onRemove(value);
                              }
                            }}
                          />
                        );
                      })}
                    </Flex>
                  }
                </div>
              </Col>
              <Col
                xs={12}
                lg={4}
                className={`border-start ps-5  ${css({ wordBreak: "break-word", overflowY: "auto", height: "600px" })}`}
              >
                <h4 className="fs-4 my-0 fw-bold d-flex align-items-center gap-2">
                  {t(($) => $.dsp["Selected Targets"])}{" "}
                  <Badge className={`badge badge-secondary`} pill>
                    {selectedTargets?.length}
                  </Badge>
                </h4>

                {selectedTargets?.map((option) => {
                  const { label, value } = option;

                  return (
                    <Checkbox
                      key={`${value}-selectedTargetsPanel`}
                      value={true}
                      label={label}
                      onChange={() => {
                        onRemove(value, "target");
                      }}
                    />
                  );
                })}
                {renderExcludeMode ?
                  <>
                    <h4 className="fs-4 my-0 fw-bold d-flex align-items-center gap-2">
                      {t(($) => $.dsp["Selected Exclusions"])}{" "}
                      <Badge className={`badge badge-secondary`} pill>
                        {selectedExclusions?.length}
                      </Badge>
                    </h4>
                    {selectedExclusions?.map((option) => {
                      const { label, value } = option;

                      return (
                        <Checkbox
                          key={`${value}-selectedExclusionsPanel`}
                          value={true}
                          label={label}
                          onChange={() => {
                            onRemove(value, "exclusion");
                          }}
                        />
                      );
                    })}
                  </>
                : null}
              </Col>
            </Row>
            <Flex justifyContent={"center"} gap={"large"} className={"pb-5"}>
              <Button
                variant="secondary"
                onPress={() => {
                  setSearch("");
                  setSearchQuery("");
                  setShowSelector(false);
                  onReset();
                }}
              >
                {t(($) => $.dsp["Cancel"])}
              </Button>
              <Button
                onPress={() => {
                  // console.log("save updated targets...");
                  setSearch("");
                  setSearchQuery("");
                  setShowSelector(false);
                }}
              >
                {t(($) => $.dsp["Confirm"])}
              </Button>
            </Flex>
          </div>
        </Modal>
      </Flex>
    </Form.Group>
  );
};

export default DSPTargetSelector;
